<script setup lang="ts">
  import { computed } from 'vue'
  import { testIds } from '@gat-ship/types'
  import { text } from '@gat-ship/stakeholders'
  import { IconLoading } from '@gat-ship/icons'
  import {
    ModalSection,
    ModalSectionHeader,
    ModalSectionHeaderTitle,
    FlagIcon,
    LinkEmail,
    LinkPhone,
    Button
  } from '@gat-ship/vue-components'
  import { useCustomFieldFormatting } from '@gat-ship/custom-fields'
  import { useStakeholderContext } from '../../../composables/useStakeholder'
  import { useStakeholderDetail } from '../../providers/stakeholder-detail-context'
  import { useStakeholderModals } from '../../providers/stakeholder-modals'
  import {
    useStakeholderEditAction,
    type StakeholderEditButtonConfig
  } from '../../../composables/useStakeholderEditAction'

  const service = useStakeholderContext()
  const { dynamicFields } = useStakeholderDetail()
  const modals = useStakeholderModals()

  const stakeholder = computed(() => service?.stakeholder.value)
  const customFieldValues = computed(
    () => service?.customFieldValues.value ?? {}
  )

  const { formatValue } = useCustomFieldFormatting()

  const { isLoading, canShow, btnConfig } = useStakeholderEditAction(
    () => stakeholder.value,
    () =>
      text.groups.generalInformation(
        stakeholder.value?.stakeholderType ?? 'client'
      ).buttons.editGeneral as StakeholderEditButtonConfig
  )
</script>

<template>
  <ModalSection v-if="stakeholder">
    <ModalSectionHeader>
      <ModalSectionHeaderTitle>
        {{ text.groups.generalInformation(stakeholder.stakeholderType).title }}
      </ModalSectionHeaderTitle>
    </ModalSectionHeader>
    <div class="divide-y">
      <div class="flex items-center justify-between gap-4 py-3">
        <span class="text-sm font-medium">Name</span>
        <span
          class="text-muted-foreground text-sm"
          :data-testid="testIds.stakeholder.sections.generalInfo.name"
          >{{ stakeholder.name }}</span
        >
      </div>
      <div class="flex items-center justify-between gap-4 py-3">
        <span class="text-sm font-medium">Short Name</span>
        <span
          class="text-muted-foreground text-sm"
          :data-testid="testIds.stakeholder.sections.generalInfo.shortName"
          >{{ stakeholder.shortName }}</span
        >
      </div>
      <div
        v-if="stakeholder.country?.code"
        class="flex items-center justify-between gap-4 py-3"
      >
        <span class="text-sm font-medium">Country</span>
        <span class="flex items-center gap-2">
          <FlagIcon
            :flag="stakeholder.country.code"
            variant="square"
            class="size-4 rounded-full"
          />
          <span
            class="text-muted-foreground text-sm"
            :data-testid="testIds.stakeholder.sections.generalInfo.country"
            >{{ stakeholder.country.name }}</span
          >
        </span>
      </div>
      <div
        v-if="dynamicFields.isSystemFieldVisible('email')"
        class="flex items-center justify-between gap-4 py-3"
      >
        <span class="text-sm font-medium">Email</span>
        <LinkEmail
          class="text-sm"
          :email="stakeholder.email ?? undefined"
          :test-id="testIds.stakeholder.sections.generalInfo.email"
        />
      </div>
      <div
        v-if="dynamicFields.isSystemFieldVisible('phoneNumber')"
        class="flex items-center justify-between gap-4 py-3"
      >
        <span class="text-sm font-medium">Phone</span>
        <LinkPhone
          class="text-sm"
          :phone-number="stakeholder.phoneNumber ?? undefined"
          :test-id="testIds.stakeholder.sections.generalInfo.phoneNumber"
        />
      </div>
      <div
        v-for="field in dynamicFields.customFields.value"
        :key="field.fieldName"
        class="flex items-center justify-between gap-4 py-3"
      >
        <span class="text-sm font-medium">{{ field.label }}</span>
        <span class="text-muted-foreground text-sm">
          {{ formatValue(field, customFieldValues[field.fieldName]) }}
        </span>
      </div>
    </div>
    <Button
      v-if="canShow"
      :variant="btnConfig.variant"
      :size="btnConfig.size"
      :class="btnConfig.class"
      :disabled="isLoading"
      :data-testid="testIds.stakeholders.actions.editGeneral"
      @click="modals?.showUpdateGeneralInfo({ stakeholder })"
    >
      <IconLoading v-if="isLoading" class="size-4 shrink-0" />
      <component
        :is="btnConfig.icon"
        v-else-if="btnConfig.icon"
        class="size-4"
      />
      {{ btnConfig.label }}
    </Button>
  </ModalSection>
</template>
